import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type Keycloak from 'keycloak-js'

// MFA types
export interface MFACredential {
  id: string
  type: 'totp' | 'sms'
  label: string
  createdAt: string
  lastUsed?: string
}

export interface MFAState {
  isRequired: boolean
  isConfigured: boolean
  availableMethods: ('totp' | 'sms')[]
  configuredMethods: MFACredential[]
  enrollmentInProgress: boolean
  enrollmentMethod?: 'totp' | 'sms'
  qrCodeData?: string
  backupCodes?: string[]
}

// Define user type based on Keycloak token claims
export interface User {
  id: string
  email: string
  firstName?: string
  lastName?: string
  username: string
  roles: string[]
  organizations?: string[]
  preferences?: {
    theme: 'light' | 'dark'
    language: string
  }
  mfa?: MFAState
}

// Authentication state interface
export interface AuthState {
  isAuthenticated: boolean
  isLoading: boolean
  token: string | null
  refreshToken: string | null
  user: User | null
  keycloakInstance: Keycloak | null
  error: string | null
  loginUrl?: string
}

// Initial state
const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  token: null,
  refreshToken: null,
  user: null,
  keycloakInstance: null,
  error: null,
}

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Set Keycloak instance
    setKeycloakInstance: (state, action: PayloadAction<Keycloak>) => {
      state.keycloakInstance = action.payload
    },

    // Login success
    loginSuccess: (
      state,
      action: PayloadAction<{ token: string; refreshToken: string; user: User }>
    ) => {
      state.isAuthenticated = true
      state.isLoading = false
      state.token = action.payload.token
      state.refreshToken = action.payload.refreshToken
      state.user = action.payload.user
      state.error = null
    },

    // Login failure
    loginFailure: (state, action: PayloadAction<string>) => {
      state.isAuthenticated = false
      state.isLoading = false
      state.token = null
      state.refreshToken = null
      state.user = null
      state.error = action.payload
    },

    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },

    // Update user profile
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload }
      }
    },

    // Token refresh
    refreshTokenSuccess: (
      state,
      action: PayloadAction<{ token: string; refreshToken: string }>
    ) => {
      state.token = action.payload.token
      state.refreshToken = action.payload.refreshToken
      state.error = null
    },

    // Logout
    logout: state => {
      state.isAuthenticated = false
      state.isLoading = false
      state.token = null
      state.refreshToken = null
      state.user = null
      state.error = null
      // Note: keycloakInstance is preserved for logout handling
    },

    // Clear error
    clearError: state => {
      state.error = null
    },

    // Set login URL (for redirecting after auth)
    setLoginUrl: (state, action: PayloadAction<string>) => {
      state.loginUrl = action.payload
    },

    // MFA actions
    setMFARequired: (state, action: PayloadAction<boolean>) => {
      if (state.user) {
        state.user.mfa = state.user.mfa || {
          isRequired: false,
          isConfigured: false,
          availableMethods: [],
          configuredMethods: [],
          enrollmentInProgress: false,
        }
        state.user.mfa.isRequired = action.payload
      }
    },

    setMFAState: (state, action: PayloadAction<Partial<MFAState>>) => {
      if (state.user) {
        state.user.mfa = { ...state.user.mfa, ...action.payload } as MFAState
      }
    },

    startMFAEnrollment: (
      state,
      action: PayloadAction<{ method: 'totp' | 'sms'; qrCodeData?: string }>
    ) => {
      if (state.user?.mfa) {
        state.user.mfa.enrollmentInProgress = true
        state.user.mfa.enrollmentMethod = action.payload.method
        if (action.payload.qrCodeData) {
          state.user.mfa.qrCodeData = action.payload.qrCodeData
        }
      }
    },

    completeMFAEnrollment: (
      state,
      action: PayloadAction<{
        credential: MFACredential
        backupCodes?: string[]
      }>
    ) => {
      if (state.user?.mfa) {
        state.user.mfa.enrollmentInProgress = false
        state.user.mfa.enrollmentMethod = undefined
        state.user.mfa.qrCodeData = undefined
        state.user.mfa.isConfigured = true
        state.user.mfa.configuredMethods.push(action.payload.credential)
        if (action.payload.backupCodes) {
          state.user.mfa.backupCodes = action.payload.backupCodes
        }
      }
    },

    cancelMFAEnrollment: state => {
      if (state.user?.mfa) {
        state.user.mfa.enrollmentInProgress = false
        state.user.mfa.enrollmentMethod = undefined
        state.user.mfa.qrCodeData = undefined
      }
    },

    removeMFACredential: (state, action: PayloadAction<string>) => {
      if (state.user?.mfa) {
        state.user.mfa.configuredMethods =
          state.user.mfa.configuredMethods.filter(
            cred => cred.id !== action.payload
          )
        state.user.mfa.isConfigured =
          state.user.mfa.configuredMethods.length > 0
      }
    },
  },
})

// Export actions
export const {
  setKeycloakInstance,
  loginSuccess,
  loginFailure,
  setLoading,
  updateUser,
  refreshTokenSuccess,
  logout,
  clearError,
  setLoginUrl,
  setMFARequired,
  setMFAState,
  startMFAEnrollment,
  completeMFAEnrollment,
  cancelMFAEnrollment,
  removeMFACredential,
} = authSlice.actions

// Export selectors
export const selectAuth = (state: { auth: AuthState }) => state.auth
export const selectIsAuthenticated = (state: { auth: AuthState }) =>
  state.auth.isAuthenticated
export const selectUser = (state: { auth: AuthState }) => state.auth.user
export const selectAuthLoading = (state: { auth: AuthState }) =>
  state.auth.isLoading
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error
export const selectUserRoles = (state: { auth: AuthState }) =>
  state.auth.user?.roles || []
export const selectKeycloakInstance = (state: { auth: AuthState }) =>
  state.auth.keycloakInstance

// Helper selectors for role checking
export const selectHasRole = (role: string) => (state: { auth: AuthState }) =>
  state.auth.user?.roles.includes(role) || false

export const selectHasAnyRole =
  (roles: string[]) => (state: { auth: AuthState }) =>
    roles.some(role => state.auth.user?.roles.includes(role)) || false

// MFA selectors
export const selectMFAState = (state: { auth: AuthState }) =>
  state.auth.user?.mfa
export const selectMFARequired = (state: { auth: AuthState }) =>
  state.auth.user?.mfa?.isRequired || false
export const selectMFAConfigured = (state: { auth: AuthState }) =>
  state.auth.user?.mfa?.isConfigured || false
export const selectMFAEnrollmentInProgress = (state: { auth: AuthState }) =>
  state.auth.user?.mfa?.enrollmentInProgress || false

// Export reducer
export default authSlice.reducer
